import { useEffect, useRef, useState } from "react"
import { useWebsocket } from "../hooks/useWebsocket"
import { SpeechTranscription } from "../lib/services/speechrecorder.service"
import { log } from "../lib/log"
import useAudioPlayer from "../hooks/useAudioPlayer"
import useIframe from "../hooks/useIframe"
import { AnimatePresence } from "framer-motion"
import CookieService from "../lib/services/cookies.services"
import { SONARIS_SESSION_KEY } from "../lib/constants"
import HandleToolCalls from "../lib/HandleTools"
import EventEmitter from 'eventemitter3'
import { LibraryConfig, LibraryContextProvider, useConfig } from "../contexts/config.context"
import { ChatItem } from "./types"
import { ActionEvent } from "../lib/_types"

// Variant 'classic'
import AgentIntroUI from "./agent-intro"
import ChatWidget from "./chat-widget"

// Variant 'modern'
import AgentIntroUI2 from "./chat-interface-2/agent-intro-2"
import CallWidget2 from "./chat-interface-2/call-widget-2"
import { AppDataStoreProvider, useAppDataStore } from "../contexts/data.context"

const useTools = true;

const ComponentVariants = {
  classic: {
    ChatWidget,
    AgentIntro: AgentIntroUI,
  },
  modern: {
    ChatWidget: CallWidget2,
    AgentIntro: AgentIntroUI2,
  }
}

export type ComponentVariantType = keyof typeof ComponentVariants

type CallWidgetProps = {
  config?: Partial<LibraryConfig>;
  variant: ComponentVariantType;
}
export const CallWidget = ({ config, variant }: CallWidgetProps) => {
  return (
    <LibraryContextProvider initialConfig={config}>
      <AppDataStoreProvider>
        <CallWidgetInternal variant={variant} />
      </AppDataStoreProvider>
    </LibraryContextProvider>
  )
}

export const CallWidgetInternal = ({ variant }: { variant: ComponentVariantType }) => {
  const { AgentIntro, ChatWidget } = ComponentVariants[variant]

  const [showChat, setShowChat] = useState(false);
  const [showCall, setShowCall] = useState(false);
  const [pttActive, setPttActive] = useState(false);
  const [isThinking, setIsThinking] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false)
  const [chatHistory, setChatHistory] = useState<ChatItem[]>([]);
  const [isMinimize, setIsMinimize] = useState(false);

  const chatEventsEmitter = useRef<EventEmitter>(new EventEmitter());

  const speechTranscriptionRef = useRef<SpeechTranscription>(new SpeechTranscription())

  const { config, updateConfig } = useConfig()

  const { iframeRef, setupFrame, navigate, isDocumentContentReady } = useIframe({ iframeId: 'sonaris-viewport' })

  const { connect, isConnected, disconnect, sendMessage, sendAudio, setActiveMode } = useWebsocket({
    config,
    url: config.websocketUrl.toString(),
    language: config.current_language,
    onMessage: handleSocketMessage,
    onClose: handleSocketClose,
    onOpen: handleSocketOpen,
    onError(event) {
      log("ws::error", event)
    },
  })

  const {
    queueBuffer,
    clearBuffer,
    stop: stopPlayback,
    connect: connectAudioPlayer,
    disconnect: disconnectAudioPlayer,
    mute: muteAudioPlayer,
    unmute: unmuteAudioPlayer,
    isPlaying,
  } = useAudioPlayer({})

  const { visualEvents } = useAppDataStore()


  useEffect(() => {
    if (config.muted) {
      muteAudioPlayer()
    } else {
      unmuteAudioPlayer()
    }
  }, [config.muted])

  function handleSocketMessage(event: MessageEvent<any>, mode: any) {
    const event_data = JSON.parse(event.data)

    log("incoming", event_data, mode)

    switch (event_data.event) {
      case 'session.created':
        const session_id = event_data.data.id
        CookieService.setCookie(SONARIS_SESSION_KEY, session_id)
        break;

      case 'audio':
        // NOTE: if tts is disabled, we will not run the audio ops and depend on the text messages
        if (mode === 'chat' || !config.remote?.character.tts) break;

        chatEventsEmitter.current.emit('audio', event_data.data)

        queueBuffer(event_data.data.b64);
        setIsThinking(false);
        break;

      case 'stt.message': {
        console.log("chatEventsEmitter.current.emit", event_data.data)
        chatEventsEmitter.current.emit('message', event_data.data)
        setChatHistory(p => ([...p, { text: event_data.data.text, role: event_data.data.role }]))

        break;
      }

      case 'message':
        // NOTE: if tts is not enabled, we will manage thinking based on text messages
        setIsThinking(false)

        chatEventsEmitter.current.emit('message', event_data.data)
        // log("SONARIS_MESSAGE", `Adding message ${event_data.data} to chat History`)
        setChatHistory(p => ([...p, { text: event_data.data.text, role: event_data.data.role }]))

        const action = event_data.data.action
        if (iframeRef.current && action && useTools) {
          const [tool, task] = action.split(':')
          log("action", tool, "||", task)

          if (action === 'buy_now') {
            visualEvents.emit("user_action:buy_now")
          } else {
            HandleToolCalls({
              iframeRef,
              tools: config.remote?.tools || {},
              task_id: task,
              toolId: tool,
              onNavStart() {
                visualEvents.emit("navigation:start")
              },
              onNavEnd() {
                visualEvents.emit("navigation:end")
              },
            })
          }
        }
        break;
    }
  }


  function handleSocketOpen() {
    log("info", "websocket open")
    connectAudioPlayer()

    setupFrame()
    if (iframeRef.current) {
      iframeRef.current.classList.add("fixed", "inset-0", "h-screen", "w-screen", "p-0", 'm-0')
      iframeRef.current.style.opacity = '0';
    }

    // NOTE: use interval to check the content frame (only required on first load of the iframe)
    const loadInt = setInterval(() => {
      if (isDocumentContentReady() && iframeRef.current) {
        console.log(Date.now(), "iframe content ready")
        iframeRef.current.style.opacity = '1';
        document.body.classList.add('sonaris-active');
        clearInterval(loadInt)
      }
    }, 250)

    speechTranscriptionRef.current.initialize()
    speechTranscriptionRef.current.setCallbacks({
      onError(error) {
        log("error", error)
      },
      onAudioAvailable(blob, duration) {
        log('audio', 'sending', duration, ' seconds of audio')
        sendAudio(blob)
        stopPlayback()
      },
      onListeningStart() {
        setPttActive(true)
      },
      onListeningStop() {
        setPttActive(false)
      },
    })

    // setup the same controls on both parent and the iframe
    // log("speechTranscriptionRef", "setting up keyboard controls for main window")
    // speechTranscriptionRef.current.setupKeyboardControls(window, "KeyV")
    //
    // if (iframeRef.current?.contentWindow) {
    //   log("speechTranscriptionRef", "setting up keyboard controls for iframe window")
    //   speechTranscriptionRef.current.setupKeyboardControls(iframeRef.current?.contentWindow, "KeyV")
    // }
    //
    // if (iframeRef.current?.contentDocument) {
    //   log("speechTranscriptionRef", "setting up keyboard controls for iframe window")
    //   speechTranscriptionRef.current.setupKeyboardControls(iframeRef.current?.contentDocument, "KeyV")
    // }

    log("info", "speechTranscriptionRef ready")

    navigate(window.location.href)
    console.log(Date.now(), "requested content loading")
    setIsConnecting(false)
  }

  function handleSocketClose() {
    speechTranscriptionRef.current.teardown()
    stopPlayback()
    clearBuffer()
    disconnectAudioPlayer()

    log("info", "websocket closed")
  }

  function handleConnect() {
    setIsConnecting(true)

    const session_id = CookieService.getCookie(SONARIS_SESSION_KEY)
    connect(session_id)

  }

  function handleDisconnect() {
    disconnect()
    CookieService.removeCookie(SONARIS_SESSION_KEY)
    updateConfig({ activeMode: null })
    setActiveMode(null)

    setChatHistory([])

    window.dispatchEvent(new ActionEvent('close-image-preview'));
    window.dispatchEvent(new ActionEvent('close-video-preview'));

    // disposeFrame()
    // document.body.classList.remove('sonaris-active')
  }


  function handlePTTStart() {
    speechTranscriptionRef.current.startRecording()
    setPttActive(true)
  }

  function handlePTTEnd() {
    setPttActive(false)
    speechTranscriptionRef.current.stopRecording()
    setIsThinking(true)
  }

  const showMainWidget = isConnected || showChat || showCall

  return (
    <div id="call-widget-container">
      <AnimatePresence>
        {
          !showMainWidget && (
            // new Agent INTRO UI for testing only u can use previouss also
            <AgentIntro
              key="agent-intro"
              source={config.remote?.character.intro_video || "https://assets.sonaris.ai/static/character-wave.mp4"}
              onCallNow={(with_audio) => {
                log('info', "AgentIntroUI::onCallNow", with_audio, config.remote?.character.tts)
                if (with_audio && config.remote?.character.tts) {
                  updateConfig({ activeMode: 'call' })
                  setActiveMode('call')
                } else {
                  updateConfig({ activeMode: 'chat' })
                  setActiveMode('chat')
                }

                handleConnect()
              }}
              connecting={isConnecting}
              setShowChat={setShowChat}
              setShowCall={setShowCall}
            />
          )
        }

        {
          showMainWidget && (
            <ChatWidget
              setShowCall={setShowCall}
              setShowChat={setShowChat}
              showChat={showChat}
              showCall={showCall}
              onClose={() => setShowChat(false)}
              isMinimize={isMinimize}
              setIsMinimize={setIsMinimize}
              onCallNow={() => {
                if (config.remote?.character.tts) {
                  updateConfig({ activeMode: 'call' })
                  setActiveMode('call')
                  handleConnect()
                } else {
                  console.error("Call mode not available", config.remote?.character.tts)
                }
              }}
              onChatNow={() => {
                updateConfig({ activeMode: 'chat' })
                setActiveMode('chat')
                handleConnect()
              }}
              handleTextInput={(text) => {
                if (!text) return;

                sendMessage({ text })
                chatEventsEmitter.current.emit('message', { role: "user", text })
                setChatHistory(p => ([...p, { text, role: 'user' }]))
                setIsThinking(true)
              }}
              handleDisconnect={handleDisconnect}
              handlePTTStart={handlePTTStart}
              handlePTTEnd={handlePTTEnd}
              isPTTActive={pttActive}
              isThinking={isThinking}
              connected={isConnected}
              connecting={isConnecting}
              chatEventsEmitter={chatEventsEmitter.current}
              isPlaying={isPlaying}
              setActiveMode={setActiveMode}
              handleConnect={handleConnect}
              chatHistory={chatHistory}
            />
          )
        }
      </AnimatePresence>
    </div>
  )
}



